import { useState, useEffect, useRef, useCallback } from 'react';
import { useCheckVersionExists } from './useFirmwareMutations';
import {
  composeVersionString,
  isValidDateTimePart,
} from '../components/firmware/versionName';

// The version check waits this long after the last date/time keystroke before firing,
// so a check is not sent on every character while the user is still typing.
const VERSION_CHECK_DEBOUNCE_MS = 500;

export type VersionStatus = 'idle' | 'checking' | 'exists' | 'available';

interface UseFirmwareVersionEditorParams {
  modelId: number;
  initialFirmwareTypeId: number | null;
  initialCountryId: string | null;
  initialNumberPart: string;
  initialDateTimePart: string;
  initialFirmwareName: string;
  // The version already persisted on the server; the availability check is skipped while the
  // edited version equals this baseline. Empty for the create flow, where nothing is saved yet.
  initialSavedVersion: string;
}

export interface UseFirmwareVersionEditorResult {
  firmwareTypeId: number | null;
  countryId: string | null;
  numberPart: string;
  dateTimePart: string;
  firmwareName: string;
  versionName: string;
  versionStatus: VersionStatus;
  isVersionComplete: boolean;
  versionChanged: boolean;
  nameChanged: boolean;
  hasUnsavedChanges: boolean;
  setFirmwareTypeId: (id: number | null) => void;
  setCountryId: (id: string | null) => void;
  setNumberPart: (value: string) => void;
  setDateTimePart: (value: string) => void;
  setFirmwareName: (value: string) => void;
  applyUploadedFile: (numberPart: string, fileName: string) => void;
  resetVersionStatus: () => void;
  markSaved: () => void;
}

// Owns the editable firmware version state (type, country, version parts, name) and the
// debounced "does this version already exist" check. The check reacts to a debounced copy
// of the date/time part so it is not fired on every keystroke, while type, country and the
// file-derived number feed it immediately.
export function useFirmwareVersionEditor({
  modelId,
  initialFirmwareTypeId,
  initialCountryId,
  initialNumberPart,
  initialDateTimePart,
  initialFirmwareName,
  initialSavedVersion,
}: UseFirmwareVersionEditorParams): UseFirmwareVersionEditorResult {
  const [firmwareTypeId, setFirmwareTypeId] = useState<number | null>(initialFirmwareTypeId);
  const [countryId, setCountryId] = useState<string | null>(initialCountryId);
  const [numberPart, setNumberPartState] = useState(initialNumberPart);
  // The edited date/time drives the input; the debounced copy drives the version check.
  const [dateTimePart, setDateTimePartState] = useState(initialDateTimePart);
  const [debouncedDateTimePart, setDebouncedDateTimePart] = useState(initialDateTimePart);
  const [firmwareName, setFirmwareName] = useState(initialFirmwareName);
  const [versionStatus, setVersionStatus] = useState<VersionStatus>('idle');
  // Baselines used to detect unsaved changes; advanced by markSaved after a successful save.
  const [savedVersion, setSavedVersion] = useState(initialSavedVersion);
  const [savedFirmwareName, setSavedFirmwareName] = useState(initialFirmwareName);

  const versionName = composeVersionString(numberPart, dateTimePart);
  const isVersionComplete = numberPart.trim() !== '' && isValidDateTimePart(dateTimePart);
  const versionChanged = versionName.trim() !== savedVersion;
  const nameChanged = firmwareName.trim() !== savedFirmwareName.trim();
  const hasUnsavedChanges = versionChanged || nameChanged;

  const checkVersion = useCheckVersionExists();
  // Kept in a ref so the check effect does not re-run when react-query recreates the mutation.
  const checkVersionRef = useRef(checkVersion);
  useEffect(() => { checkVersionRef.current = checkVersion; });
  // Increments per check so a slow earlier response cannot overwrite a newer result.
  const checkRequestIdRef = useRef(0);

  const resetVersionStatus = useCallback(() => setVersionStatus('idle'), []);

  // Applies each date/time keystroke immediately and clears any stale check result while
  // typing; the debounce effect below schedules the next check once typing settles.
  const setDateTimePart = useCallback((value: string) => {
    setDateTimePartState(value);
    setVersionStatus('idle');
  }, []);

  // Manual override of the file-derived number (create view only); the check reacts immediately
  // since the number is a direct dependency of the check effect.
  const setNumberPart = useCallback((value: string) => {
    setNumberPartState(value);
    setVersionStatus('idle');
  }, []);

  // Locks in the number and name derived from a freshly selected file and flushes the debounce
  // so the check runs at once instead of waiting out the date/time debounce window.
  const applyUploadedFile = useCallback((derivedNumber: string, fileName: string) => {
    setNumberPartState(derivedNumber);
    setFirmwareName(fileName);
    setVersionStatus('idle');
    setDebouncedDateTimePart(dateTimePart);
  }, [dateTimePart]);

  // Advances the saved baselines so the "unsaved changes" state clears immediately after a save,
  // without waiting for the firmware list to refetch.
  const markSaved = useCallback(() => {
    setSavedVersion(versionName.trim());
    setSavedFirmwareName(firmwareName.trim());
    setVersionStatus('idle');
  }, [versionName, firmwareName]);

  // Debounce the edited date/time into the value the check reacts to.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDateTimePart(dateTimePart), VERSION_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [dateTimePart]);

  // Runs the availability check whenever the type, country or (debounced) version changes.
  // Only complete versions that differ from the saved baseline are checked. The check status
  // is inherently asynchronous, so setting it from within this effect is deliberate.
  useEffect(() => {
    const candidateVersion = composeVersionString(numberPart, debouncedDateTimePart);
    const isCandidateComplete = numberPart.trim() !== '' && isValidDateTimePart(debouncedDateTimePart);
    const shouldCheck = isCandidateComplete && firmwareTypeId !== null && candidateVersion !== savedVersion;
    if (!shouldCheck) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVersionStatus('idle');
      return;
    }

    const requestId = ++checkRequestIdRef.current;
    setVersionStatus('checking');
    checkVersionRef.current.mutate(
      { modelId, firmwareTypeId, countryId, versionName: candidateVersion },
      {
        onSuccess: (exists) => {
          if (checkRequestIdRef.current !== requestId) return;
          setVersionStatus(exists ? 'exists' : 'available');
        },
        onError: () => {
          if (checkRequestIdRef.current !== requestId) return;
          setVersionStatus('idle');
        },
      }
    );
  }, [modelId, firmwareTypeId, countryId, numberPart, debouncedDateTimePart, savedVersion]);

  return {
    firmwareTypeId,
    countryId,
    numberPart,
    dateTimePart,
    firmwareName,
    versionName,
    versionStatus,
    isVersionComplete,
    versionChanged,
    nameChanged,
    hasUnsavedChanges,
    setFirmwareTypeId,
    setCountryId,
    setNumberPart,
    setDateTimePart,
    setFirmwareName,
    applyUploadedFile,
    resetVersionStatus,
    markSaved,
  };
}
